import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Building2, AlertCircle, Eye, EyeOff, Lock, Mail, Shield } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Password visibility toggle with reduced motion support
  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  // Form validation
  const validateForm = () => {
    let isValid = true;
    setEmailError('');
    setPasswordError('');

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    }

    // Password validation
    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await login(email, password);
      if (result.success) {
        // Handle remember me
        if (rememberMe) {
          localStorage.setItem('remembered_email', email);
        } else {
          localStorage.removeItem('remembered_email');
        }
        navigate('/');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Restore remembered email on mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('remembered_email');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 p-4 mesh-gradient">
      <Card variant="elevated" className="w-full max-w-md">
        <CardHeader className="space-y-4 text-center pb-6">
          <div className="flex justify-center">
            <div className="p-4 bg-primary-100 rounded-2xl shadow-sm">
              <Building2 className="h-12 w-12 text-primary-600" />
            </div>
          </div>
          <div className="space-y-2">
            <CardTitle className="text-3xl font-display font-bold text-text-primary">
              Hospital Inventory Suite
            </CardTitle>
            <p className="text-base text-text-secondary">
              Sign in to manage your hospital inventory
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {/* Global Error Alert */}
            {error && (
              <div className="p-4 rounded-lg bg-red-50 border-2 border-red-200 flex items-start gap-3" role="alert" aria-live="assertive">
                <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold text-red-900">Authentication Error</p>
                  <p className="text-sm text-red-800 mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Email Field */}
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-semibold text-primary-800 flex items-center gap-2">
                <Mail className="h-4 w-4" aria-hidden="true" />
                Email Address
                <span className="text-red-600" aria-label="required">*</span>
              </label>
              <Input
                id="email"
                type="email"
                placeholder="name@hospital.org"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
                required
                autoComplete="email"
                autoFocus
                className="h-12 text-base"
                aria-describedby="email-hint email-error"
                aria-invalid={!!emailError}
                disabled={isSubmitting}
              />
              <p id="email-hint" className="text-xs text-text-muted">
                Use your institutional email address
              </p>
              {emailError && (
                <p id="email-error" className="text-sm text-red-600 flex items-center gap-1" role="alert">
                  <AlertCircle className="h-3 w-3" aria-hidden="true" />
                  {emailError}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-semibold text-primary-800 flex items-center gap-2">
                <Lock className="h-4 w-4" aria-hidden="true" />
                Password
                <span className="text-red-600" aria-label="required">*</span>
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (passwordError) setPasswordError('');
                  }}
                  required
                  autoComplete="current-password"
                  className="h-12 text-base pr-12"
                  aria-describedby="password-hint password-error"
                  aria-invalid={!!passwordError}
                  disabled={isSubmitting}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 p-0"
                  onClick={togglePasswordVisibility}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  disabled={isSubmitting}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden="true" />
                  )}
                </Button>
              </div>
              <p id="password-hint" className="text-xs text-text-muted">
                Minimum 6 characters
              </p>
              {passwordError && (
                <p id="password-error" className="text-sm text-red-600 flex items-center gap-1" role="alert">
                  <AlertCircle className="h-3 w-3" aria-hidden="true" />
                  {passwordError}
                </p>
              )}
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember-me"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                  disabled={isSubmitting}
                  aria-describedby="remember-me-hint"
                />
                <label htmlFor="remember-me" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Remember me
                </label>
              </div>
              <Button
                type="button"
                variant="ghost"
                className="text-sm font-medium text-accent-600 hover:text-accent-700 p-0 h-auto"
                onClick={() => navigate('/forgot-password')}
                disabled={isSubmitting}
              >
                Forgot password?
              </Button>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-12 text-base font-semibold shadow-md hover:shadow-lg active:shadow-none transition-all duration-200"
              size="lg"
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting || isLoading ? (
                <>
                  <Spinner className="mr-2" size="sm" />
                  Signing in...
                </>
              ) : (
                <>Sign in</>
              )}
            </Button>
          </form>

          {/* Demo Credentials Panel */}
          <div className="pt-4 border-t border-border mt-2">
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <p className="font-semibold text-blue-900 mb-3 flex items-center justify-center gap-2">
                <Shield className="w-4 h-4" aria-hidden="true" />
                Demo Accounts
                <span className="text-xs font-normal text-blue-700 ml-1">(for testing)</span>
              </p>
              <div className="space-y-2 text-sm text-blue-800">
                <div className="flex items-center justify-between">
                  <span className="font-mono bg-white px-2 py-1 rounded border border-blue-200">admin@hospital.org</span>
                  <span className="font-mono bg-white px-2 py-1 rounded border border-blue-200">admin123</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-mono bg-white px-2 py-1 rounded border border-blue-200">manager@hospital.org</span>
                  <span className="font-mono bg-white px-2 py-1 rounded border border-blue-200">manager123</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-mono bg-white px-2 py-1 rounded border border-blue-200">staff@hospital.org</span>
                  <span className="font-mono bg-white px-2 py-1 rounded border-blue-200">staff123</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-text-muted text-center mt-4">
              This system integrates with hospital-erp authentication.
              <br />
              Contact your administrator for access.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
